import { logger } from "@/utils/logger";
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IsolatedDatePicker } from '@/components/ui/isolated-datepicker';
import { IsolatedTimePicker } from '@/components/ui/isolated-timepicker';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFormContext } from '@/contexts/FormContext';
import { createStandardizedFormSubmission } from '@/utils/formSubmissionUtils';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { useInterestDataSync } from '@/hooks/useInterestData';
import CollapsibleOpeningHours from '@/components/messages/CollapsibleOpeningHours';
import { FormField } from './shared/FormField';
import { toast } from 'sonner';
import type { DepartmentHours } from '@/utils/messageParser/types';

interface AppointmentFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  openingHours?: DepartmentHours[];
  title?: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  formData,
  onUpdateData,
  currentStep,
  onStepChange,
  onSubmit,
  openingHours,
}) => {
  const { t, language } = useLanguage();
  const { closeForm } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const storedInterestData = useInterestDataSync();
  const totalSteps = 2;
  
  // Uncontrolled input refs for name and phone
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  
  // Sync local state when formData changes but stop once user starts typing
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);
  
  // Format time display function
  const formatTimeDisplay = (hour: number, minute: number) => {
    const isPM = hour >= 12;
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const minuteStr = `:${String(minute).padStart(2, '0')}`;
    return `${displayHour}${minuteStr}${isPM ? 'pm' : 'am'}`;
  };

  // Convert AM/PM time to 24-hour format
  const convertTo24Hour = (hour: number, ampm: string) => {
    if (ampm === 'pm' && hour !== 12) return hour + 12;
    if (ampm === 'am' && hour === 12) return 0;
    return hour;
  };

  // Memoize default appointment slots to prevent recreation on every render
  const defaultAppointmentSlots = useMemo(() => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const displayTime = formatTimeDisplay(hour, minute);
        slots.push({ value: timeString, label: displayTime });
      }
    }
    return slots;
  }, []);

  // Generate appointment slots from hours data  
  const generateAppointmentSlotsFromHours = useCallback((salesDayHours: any) => {
    // Check if sales is closed
    if (salesDayHours.opening === '00:00' && salesDayHours.closing === '00:00') {
      return defaultAppointmentSlots;
    }
    
    // Parse and convert opening/closing times
    const openingHour = parseInt(salesDayHours.opening.split(':')[0]);
    const openingMinute = parseInt(salesDayHours.opening.split(':')[1]);
    const closingHour = parseInt(salesDayHours.closing.split(':')[0]);
    const closingMinute = parseInt(salesDayHours.closing.split(':')[1]);
    
    const openingHour24 = convertTo24Hour(openingHour, salesDayHours.opening_am_pm);
    const closingHour24 = convertTo24Hour(closingHour, salesDayHours.closing_am_pm);
    
    // Generate 30-minute slots
    const slots = [];
    let currentHour = openingHour24;
    let currentMinute = openingMinute;
    
    while (currentHour < closingHour24 || (currentHour === closingHour24 && currentMinute < closingMinute)) {
      const timeString = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
      const displayTime = formatTimeDisplay(currentHour, currentMinute);
      slots.push({ value: timeString, label: displayTime });
      
      // Add 30 minutes
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour += 1;
      }
    }
    
    return slots;
  }, [defaultAppointmentSlots]);

  const [timeSlots, setTimeSlots] = useState<Array<{value: string, label: string}>>(
    () => defaultAppointmentSlots
  );
  
  // Memoize input handlers for better performance
  const handleInputChange = useCallback((field: string, value: string) => {
    if (!hasUserEdited && (field === 'name' || field === 'phone')) {
      setHasUserEdited(true);
    }
    // Clear validation error when user starts typing
    setValidationErrors(prev => {
      if (prev.has(field)) {
        const newErrors = new Set(prev);
        newErrors.delete(field);
        return newErrors;
      }
      return prev;
    });
    onUpdateData({ [field]: value });
  }, [onUpdateData, hasUserEdited]);

  const handleDateChange = useCallback((date: Date | undefined) => {
    if (date) {
      // Fix timezone issue by using local date string
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onUpdateData({ appointmentDate: `${year}-${month}-${day}` });
    }
  }, [onUpdateData]);

  // Memoize sales hours processing function
  const getSalesHoursForDate = useCallback((date: string) => {
    try {
      if (!date || !openingHours?.length) return null;
      
      const [year, month, day] = date.split('-').map(Number);
      if (!year || !month || !day) {
        return null;
      }
      
      const dateObj = new Date(year, month - 1, day);
      const dayOfWeek = dateObj.getDay();
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      const salesDept = openingHours.find(dept => 
        dept.Department === 'Sales' || 
        dept.Department === 'New' || 
        dept.Department === 'Ventes'
      );
      return salesDept?.Days.find(day => day.day === adjustedDay);
    } catch (error) {
      console.error('Error in getSalesHoursForDate:', error);
      return null;
    }
  }, [openingHours]);

  // Optimized time slots computation with minimal logging
  useEffect(() => {
    const selectedDate = formData.appointmentDate;
    
    if (!selectedDate) {
      setTimeSlots(defaultAppointmentSlots);
      setIsLoadingTimeSlots(false);
      return;
    }

    setIsLoadingTimeSlots(true);
    
    try {
      const salesDayHours = getSalesHoursForDate(selectedDate);
      const slots = salesDayHours ? generateAppointmentSlotsFromHours(salesDayHours) : defaultAppointmentSlots;
      setTimeSlots(slots);
      setIsLoadingTimeSlots(false);
    } catch (error) {
      console.error('Error computing appointment time slots:', error);
      setTimeSlots(defaultAppointmentSlots);
      setIsLoadingTimeSlots(false);
    }
  }, [formData.appointmentDate, openingHours, generateAppointmentSlotsFromHours, defaultAppointmentSlots, getSalesHoursForDate]);

  const getSelectedDate = () => {
    if (!formData.appointmentDate) return undefined;
    const [year, month, day] = formData.appointmentDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getTimeValue = () => formData.appointmentTime || '';

  const handleNext = () => {
    
    if (!isStepValid()) {
      // Mark invalid fields
      const errors = new Set<string>();
      if (currentStep === 0) {
        if (!formData.email?.trim() || !formData.email.includes('@')) errors.add('email');
        if (!formData.appointmentDate?.trim()) errors.add('appointmentDate');
        if (!formData.appointmentTime?.trim()) errors.add('appointmentTime');
      } else if (currentStep === 1) {
        if (!formData.name?.trim()) errors.add('name');
        if (!formData.phone?.trim()) errors.add('phone');
      }
      logger.log('🔴 AppointmentForm - Validation failed, errors:', Array.from(errors));
      setValidationErrors(errors);
      return;
    }
    logger.log('✅ AppointmentForm - Validation passed, moving to next step');
    setValidationErrors(new Set());
    if (currentStep < totalSteps - 1) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleFormSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const name = nameInputRef.current?.value || '';
      const phone = phoneInputRef.current?.value || '';
      
      // Create appointment interest data with department from stored data
      const appointmentMessage = language === 'fr'
        ? `Demande de rendez-vous pour le ${formData.appointmentDate} à ${formData.appointmentTime}`
        : `Appointment request for ${formData.appointmentDate} at ${formData.appointmentTime}`;
      
      const appointmentInterestData = {
        message: appointmentMessage,
        department: storedInterestData?.department || 'New',
        ...storedInterestData,
        serviceDetails: {
          date: formData.appointmentDate || '',
          time: formData.appointmentTime || '',
          email: formData.email || ''
        }
      };

      const submissionData = createStandardizedFormSubmission(
        {
          name,
          phone,
          email: formData.email || '',
          appointmentDate: formData.appointmentDate || '',
          appointmentTime: formData.appointmentTime || '',
          interest: formData.interest || ''
        },
        'appointment',
        formatPhoneNumber,
        appointmentInterestData
      );
      
      if (onSubmit) {
        await onSubmit(submissionData);
      }
      
      onUpdateData({ ...formData, name, phone });
      
      // Show success message and close form
      toast.success(language === 'fr' ? 'Demande envoyée avec succès!' : 'Request sent successfully!');
      
      // Close form after short delay to let user see success feedback
      setTimeout(() => {
        closeForm(true);
      }, 500);
    } catch (error) {
      console.error('Appointment form submission error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    const name = nameInputRef.current?.value || '';
    const phone = phoneInputRef.current?.value || '';
    
    switch (currentStep) {
      case 0:
        return formData.email?.trim().length > 0 && 
               formData.email.includes('@') &&
               formData.appointmentDate?.trim().length > 0 &&
               formData.appointmentTime?.trim().length > 0;
      case 1:
        return name.trim().length > 0 && phone.trim().length > 0;
      default:
        return false;
    }
  };

  // Check if a date should be disabled
  const isDateDisabled = (date: Date) => {
    if (date < new Date()) return true;
    
    // If no opening hours data, allow weekdays (Monday-Friday)
    if (!openingHours?.length) {
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // Disable only weekends
    }
    
    const dayOfWeek = date.getDay();
    const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    
    const salesDept = openingHours.find(dept => 
      dept.Department === 'Sales' || 
      dept.Department === 'New' || 
      dept.Department === 'Ventes'
    );
    const dayHours = salesDept?.Days.find(day => day.day === adjustedDay);
    
    // If no specific day found but we have some opening hours, allow weekdays as fallback
    if (!dayHours) {
      return dayOfWeek === 0 || dayOfWeek === 6; // Disable only weekends
    }
    
    return dayHours.opening === '00:00' && dayHours.closing === '00:00';
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-2">
            {/* Opening Hours Table - Show all departments */}
            {openingHours && openingHours.length > 0 && (
              <div className="mb-3">
                <CollapsibleOpeningHours 
                  openingHours={openingHours} 
                  triggerLabel={t('hours.appointment')}
                />
              </div>
            )}
            
            {/* Hidden Interest field */}
            <input type="hidden" name="interest" value={formData.interest || ''} />
            
            {/* Hidden topcarid field */}
            <input 
              id="topcarid" 
              name="topcarid" 
              type="hidden" 
              value={(() => {
                try {
                  const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
                  return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
                } catch {
                  return '';
                }
              })()}
            />
            
            {/* Date and Time side by side at the top */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className={cn("text-xs", validationErrors.has('appointmentDate') ? "text-red-500" : "text-gray-700")}>{t('form.appointmentDate')}</Label>
                <div className="d2cbot-datepicker-wrapper">
                  <IsolatedDatePicker
                    value={formData.appointmentDate}
                    onChange={(date) => handleInputChange('appointmentDate', date)}
                    disabled={isDateDisabled}
                    placeholder={t('form.selectDate')}
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="appointmentTime" className={cn("text-xs", validationErrors.has('appointmentTime') ? "text-red-500" : "text-gray-700")}>{t('form.appointmentTime')}</Label>
                <IsolatedTimePicker
                  value={formData.appointmentTime}
                  onChange={(time) => handleInputChange('appointmentTime', time)}
                  timeSlots={timeSlots}
                  variant="dropdown"
                  loading={isLoadingTimeSlots}
                  placeholder={t('form.selectTime')}
                  className="w-full"
                />
              </div>
            </div>
            
            {/* Email field at the bottom */}
            <FormField
              id="email"
              name="email"
              label={t('form.email')}
              type="email"
              autoComplete="email"
              value={formData.email || ''}
              onChange={(value) => handleInputChange('email', value)}
              placeholder="exemple@email.com"
              error={validationErrors.has('email')}
            />
          </div>
        );
      case 1:
        return (
          <div className="space-y-2">
            {/* Side by side fields */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="name" className={cn("text-xs", validationErrors.has('name') ? "text-red-500" : "text-gray-700")}>{t('form.fullName')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  defaultValue={localFormData.name || ''}
                  ref={nameInputRef}
                  onChange={() => handleInputChange('name', nameInputRef.current?.value || '')}
                  placeholder={t('form.fullName')}
                  className="h-7 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone" className={cn("text-xs", validationErrors.has('phone') ? "text-red-500" : "text-gray-700")}>{t('form.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  defaultValue={localFormData.phone || ''}
                  ref={phoneInputRef}
                  onChange={() => handleInputChange('phone', phoneInputRef.current?.value || '')}
                  placeholder="(555) 555-1212"
                  className="h-7 text-sm"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  

  return (
    <div className="p-0.9">
      <div className="mb-2">
        {renderStep()}
      </div>
      
      <div className="flex items-center justify-between">
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            className="h-6 px-2 text-xs"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            {t('form.back')}
          </Button>
        )}
        
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full ${
                index === currentStep
                  ? 'bg-black'
                  : index < currentStep
                  ? 'bg-gray-400'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {!isLastStep ? (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              handleNext();
            }}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {t('form.next')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleFormSubmit}
            disabled={!isStepValid() || isSubmitting}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                {t('form.submitting')}
              </>
            ) : (
              t('form.submit')
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AppointmentForm;